var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
	var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
	if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
	else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
	return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, configure, makeObservable } from 'mobx';
configure({ isolateGlobalState: true });
import { GlobalCache } from './ModelCache';
import { getPrivateKey, getPrivateTriggerKey, getModelClassResolvableCollection, getConstructorOf } from './Utils';
import { Model } from "@mathquis/modelx";
const symProp = Symbol.for('modelx:resolvables');
const defaults = {
	cache: false,
	local: false,
	log: false,
	logger: console
};
export default class AbstractResolvableModel extends Model {
	collection = undefined;
	_resolvableCollection;
	_resolvingCollection = new Map();
	static getDefaults() {
		return defaults;
	}
	get cache() {
		return this.ensureResolvableCollection().cache;
	}
	constructor(attributes = {}, options = {}) {
		super(attributes, options);
		makeObservable(this);
	}
	set(attribute, value, options) {
		super.set(attribute, value, options);
		if (!options?.silent) {
			this.updateResolvables();
		}
		return this;
	}
	unset(attribute) {
		super.unset(attribute);
		this.updateResolvables();
		return this;
	}
	reset(attributes, options = { dirty: true }) {
		super.reset(attributes, options);
		this.updateResolvables();
		return this;
	}
	updateResolvables() {
		const resolvables = this.getAllResolvableOptions();
		Object.entries(resolvables).forEach(resolvable => {
			const [propName, options] = resolvable;
			const privateKey = getPrivateKey(propName);
			if (!this[privateKey]) {
				return;
			}
			const attributeName = options.attributeName;
			const attribute = this.get(attributeName);
			const model = this.ensureResolvableModel(propName, attributeName, attribute, options);
			if (this[privateKey].constructor === model.constructor && this[privateKey].id === model.id) {
				return;
			}
			this.setResolvable(propName, model);
		});
	}
	getResolvableId(propName) {
		const resolvableOptions = this.getResolvableOptions(propName);
		if (!resolvableOptions) {
			return;
		}
		const attributeName = resolvableOptions.attributeName || propName;
		const attribute = this.get(attributeName);
		const attributes = this.getResolvableAttributes(propName, attributeName, attribute);
		return attributes?.id;
	}
	ensureResolvableCollection() {
		if (this.collection) {
			return this.collection;
		}
		if (!this._resolvableCollection) {
			const thisClass = getConstructorOf(this);
			this._resolvableCollection = getModelClassResolvableCollection(thisClass, [this]);
		}
		if (!this._resolvableCollection) {
			throw new Error(`${this.constructor.name} does not provide a resolvable collection`);
		}
		return this._resolvableCollection;
	}
	ensureResolvableModel(propName, attributeName, attribute, options) {
		const defaultModelClass = options.model;
		const modelClass = this.getResolvableModelClass(propName, attributeName, attribute);
		if (typeof modelClass === 'undefined') {
			const unresolvedModelClass = options.unresolved || defaultModelClass;
			return new unresolvedModelClass({}, { loaded: true });
		}
		else {
			const attributes = this.getResolvableAttributes(propName, attributeName, attribute);
			const resolvingModelClass = modelClass || defaultModelClass;
			if (options.cache && attributes && attributes.id) {
				const cacheHandler = options.local ? this.ensureResolvableCollection().cache : GlobalCache;
				const cachedModel = cacheHandler.get(resolvingModelClass, attributes.id);
				if (cachedModel) {
					return cachedModel;
				}
			}
			return new resolvingModelClass(attributes);
		}
	}
	validateResolvableModel(propName, resolvable, model) {
		if (!(model instanceof resolvable.model) || !(resolvable.unresolved && model instanceof resolvable.unresolved)) {
			const modelClass = getConstructorOf(model);
			if (Array.isArray(resolvable.allow) && resolvable.allow.indexOf(modelClass) < 0) {
				throw new Error(`${this.constructor.name} resolvable "${propName}" does not allow "${modelClass.name}". Add it to the resolvable "allow" option`);
			}
		}
		return resolvable;
	}
	resolveProperty(propName) {
		const options = this.getResolvableOptions(propName);
		if (!options) {
			throw new Error(`${this.constructor.name} property "${propName}" is not a resolvable`);
		}
		this.ensureResolvableCollection().resolveProperty(propName, options);
	}
	getAllResolvableOptions() {
		return this.constructor[symProp] || {};
	}
	getResolvableOptions(propName) {
		return this.getAllResolvableOptions()[propName];
	}
	setResolvable(propName, model) {
		const resolvable = this.getResolvableOptions(propName);
		if (!resolvable) {
			throw new Error(`Property "${propName}" of ${this.constructor.name} is not a resolvable`);
		}
		this.validateResolvableModel(propName, resolvable, model);
		if (model.constructor != resolvable.unresolved) {
			if (!!resolvable.cache && model.isLoaded) {
				const local = !!resolvable.local;
				const cacheHandler = local ? this.ensureResolvableCollection().cache : GlobalCache;
				cacheHandler.set(model);
			}
		}
		const privateKey = getPrivateKey(propName);
		this[privateKey] = model;

		model.propName = propName;
		model.calledBy = this;

		const attributeName = resolvable.attributeName;
		this.set(attributeName, this.getAttributesFromModel(propName, attributeName, model), { silent: true });
		this.triggerResolvable(propName);
	}
	triggerResolvable(propName) {
		const privateTrigger = getPrivateTriggerKey(propName);
		this[privateTrigger] = Date.now() + Math.random();
	}
	static getResolvableCollection() {
		throw new Error(`${this.constructor.name} needs to implement the method "getResolvableCollection()"`);
	}
	getResolvableAttributes(propName, attributeName, attribute) {
		if (!(typeof attribute === 'object')) {
			return { id: attribute };
		}
		return attribute;
	}
	getResolvableModelClass(propName, attributeName, attribute) {
		return null;
	}
	getAttributesFromModel(propName, attributeName, model) {
		return model.attributes;
	}
}
__decorate([
	action
], AbstractResolvableModel.prototype, "triggerResolvable", null);
const resolvable = function (defaultModelClass, options = {}) {
	return function (target, key) {
		const attributeName = options.attributeName || key;
		const privateKey = getPrivateKey(key);
		const privateTrigger = getPrivateTriggerKey(key);
		const allow = options.allow;
		const opts = {
			...defaults,
			...options,
			model: defaultModelClass,
			attributeName,
			allow
		};
		let properties = {};
		if (Object.prototype.hasOwnProperty.call(target.constructor, symProp)) {
			properties = target.constructor[symProp];
		}
		else {
			let c = target.constructor;
			while (c.prototype instanceof AbstractResolvableModel) {
				properties = target.constructor[symProp] = { ...c[symProp], ...properties };
				c = Object.getPrototypeOf(c);
			}
		}
		properties[key] = opts;
		function getter() {
			const trigger = this[privateTrigger];
			trigger;
			let model = this[privateKey];
			if (!model) {
				const attribute = this.get(attributeName);
				const attributes = this.getResolvableAttributes(key, attributeName, attribute);
				model = this.ensureResolvableModel(key, attributeName, attribute, opts);
				this.validateResolvableModel(key, opts, model);
				this[privateKey] = model;
			}
			if (!this[privateKey].isLoaded && (this[privateKey].id || opts.unresolved)) {
				this.resolveProperty(key);
			}

			model.propName = key;
			model.calledBy = this;

			return model;
		}
		function setter(model) {
			this.setResolvable(key, model);
		}
		Object.defineProperty(target, privateTrigger, {
			value: null,
			configurable: true,
			writable: true
		});
		observable(target, privateTrigger);
		Object.defineProperty(target, key, {
			get: getter,
			set: setter,
			configurable: true
		});
		computed(target, key);
	};
};
export { resolvable };
//# sourceMappingURL=AbstractResolvableModel.js.map